import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getAllServiceProducts } from "../../../../api/action/products";
import customSticthing from "./service1.jpg";
import sareeToMaxiImg from "./service2.jpg";
import sareeConversionImg from "./service3.jpg";
import defaultBlouseImg from "./blouse.jpg";
import { academyLogger } from "utils/logger";
import { formatPhoneNumber } from "../../../../config/locationConfig";

const AvikarServiceDetails = () => {
    const [isVisible, setIsVisible] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const companyDetail = useSelector(
        (state) => state.companyReducer.company
    );
    const companyId = companyDetail?.primaryId;

    // Get products from Redux store
    const { products = [], loading = false, error = null } = useSelector(state => state.products || {});

    // Filter service products
    const serviceProducts = products.filter(product =>
        product.type?.typeCode === "SERVICE" && product.status === "ACTIVE"
    );

    useEffect(() => {
        setIsVisible(true);

        // Fetch service products when component mounts
        const fetchServiceProducts = async () => {
            try {
                if (companyId) {
                    await dispatch(getAllServiceProducts(companyId));
                }
            } catch (err) {
                academyLogger.error("Failed to fetch service products:", err);
            }
        };

        fetchServiceProducts();
    }, [companyId, dispatch]);

    // Function to handle booking navigation
    const handleBookConsultation = () => {
        navigate('/contact');
    };

    // Function to handle service booking with service name
    const handleBookService = (serviceName = "") => {
        navigate('/contact', {
            state: {
                service: serviceName,
                bookingType: 'service'
            }
        });
    };

    // Function to open product detail modal
    const handleReadMore = (product) => {
        setSelectedProduct(product);
        setShowModal(true);
        // Prevent body scroll when modal is open
        document.body.style.overflow = 'hidden';
    };

    // Function to close modal
    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedProduct(null);
        // Restore body scroll
        document.body.style.overflow = 'auto';
    };

    // Function to get display phone number with country code
    const getDisplayPhoneNumber = () => {
        const rawPhoneNumber =
            companyDetail?.contactPerson?.phoneNumber ||
            companyDetail?.contactPerson?.mobile ||
            companyDetail?.phoneNumber ||
            "";

        const rawCountryCode =
            companyDetail?.contactPerson?.countryCode ||
            companyDetail?.countryCode ||
            "";

        return rawPhoneNumber
            ? formatPhoneNumber(rawPhoneNumber, rawCountryCode || companyDetail)
            : "Contact Number";
    };

    // Extract values from optional attributes
    const extractValues = (valuesArray) => {
        if (!Array.isArray(valuesArray) || valuesArray.length === 0) return [];
        return valuesArray.map(item => {
            if (typeof item === 'string') return item;
            if (typeof item === 'object' && item !== null) {
                return item.value || item.name || item.label || item.title || item.code || 'N/A';
            }
            return String(item);
        });
    };

    // Check if attribute has color values
    const hasColorValues = (attr) => {
        if (!attr.values || !Array.isArray(attr.values)) return false;
        const extractedValues = extractValues(attr.values);
        return extractedValues.some(val =>
            typeof val === 'string' && (
                /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(val) ||
                ['red', 'blue', 'green', 'yellow', 'black', 'white', 'purple', 'pink', 'orange', 'brown', 'gray', 'grey'].includes(val.toLowerCase())
            )
        );
    };

    // Get contact person details from company
    const contactPersonName = companyDetail?.contactPerson?.name ||
        companyDetail?.contactPerson?.firstName + " " + companyDetail?.contactPerson?.lastName;

    const email = companyDetail?.contactPerson?.email ||
        companyDetail?.email ||
        "support@example.com";

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-rose-50 flex items-center justify-center">
                <div className="text-2xl text-rose-600 animate-pulse">Loading services...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-rose-50 flex items-center justify-center">
                <div className="text-2xl text-red-600">Error loading services: {error.message}</div>
            </div>
        );
    }

    // Service sections data with specific images - NO EFFECTS on images
    const serviceSections = [
        {
            id: "custom-stitching",
            title: "CUSTOM STITCHING",
            subtitle: "For Women & Kids",
            description: "Kurti, Lehenga, Maxi, Saree Blouse, Indian & Indo-Western Designs",
            features: ["Traditional & Contemporary Designs", "Perfect Fit Guaranteed", "Premium Quality Fabrics"],
            image: customSticthing,
            gradientFrom: "rose",
            gradientTo: "amber",
            buttonLabel: "Book Custom Stitching"
        },
        {
            id: "saree-services",
            title: "SAREE SERVICES",
            subtitle: "Expert Craftsmanship",
            description: "Blouse Stitching, Saree Pre-pleating, Saree to Maxi conversion, Falls & Pico",
            features: ["Expert Blouse Crafting", "Precision Pleating", "Creative Conversions"],
            image: sareeToMaxiImg,
            gradientFrom: "violet",
            gradientTo: "fuchsia",
            buttonLabel: "Book Saree Service"
        },
        {
            id: "quick-solutions",
            title: "QUICK SOLUTIONS",
            subtitle: "Instant Transformations",
            description: "Alterations, One-minute saree conversion, Temporary or Permanent Saree Pre-pleating",
            features: ["Instant Alterations", "Quick Fix Solutions", "Emergency Services"],
            image: sareeConversionImg,
            gradientFrom: "orange",
            gradientTo: "red",
            buttonLabel: "Book Quick Service"
        }
    ];

    // Additional service for Saree to Maxi
    const extraServices = [
        {
            title: "Saree to Maxi Conversion",
            description: "Transform your favorite saree into a stylish maxi dress. Perfect for a modern, chic look while preserving the beauty of your saree.",
            image: sareeToMaxiImg,
            tags: ["Creative", "Custom Fit", "Trendy"]
        }
    ];

    return (
        <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-rose-50 py-16 px-4 sm:px-8 lg:px-16 font-sans text-gray-800 overflow-hidden relative">
            {/* Animated Background Elements */}
            <div className="fixed inset-0 -z-50 overflow-hidden">
                <div className="absolute -top-40 -right-32 w-80 h-80 bg-amber-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob"></div>
                <div className="absolute -bottom-40 -left-32 w-80 h-80 bg-rose-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-2000"></div>
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-violet-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-4000"></div>
            </div>

            {/* Hero Section - Updated heading style with solid color */}
            <div className={`text-center mt-16 mb-20 transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'} relative z-10`}>
                <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-6 text-gray-800">
                    Professional Stitching
                    <br />
                    & Design Services
                </h1>
                <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed px-4">
                    Crafting perfection in every stitch with traditional elegance and modern precision.
                    From custom ensembles to quick saree transformations, we bring your vision to life.
                </p>
            </div>

            {/* Main Service Sections - ORIGINAL IMAGES WITHOUT ANY EFFECTS */}
            {serviceSections.map((section, idx) => (
                <div
                    key={section.id}
                    className={`flex flex-col ${idx % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-stretch justify-between mb-28 gap-12 transform transition-all duration-1000 delay-${idx * 200} ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'} relative z-10`}
                >
                    {/* Image Panel - NO OVERLAYS, NO FILTERS, ORIGINAL IMAGE */}
                    <div className="lg:w-1/2 relative group">
                        <div className="relative h-full rounded-2xl overflow-hidden shadow-2xl">
                            <img
                                src={section.image}
                                alt={section.title}
                                className="w-full h-full object-cover"
                            />
                        </div>
                        {/* Floating badge */}
                        <div className={`absolute -bottom-4 -right-4 bg-white rounded-full px-4 py-2 shadow-lg flex items-center gap-2 z-20`}>
                            <div className={`w-2 h-2 rounded-full bg-${section.gradientFrom}-500 animate-pulse`}></div>
                            <span className="text-sm font-medium text-gray-700">Expert Service</span>
                        </div>
                    </div>

                    {/* Content Panel */}
                    <div className="lg:w-1/2 flex items-center">
                        <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 md:p-10 shadow-xl border border-white/50 hover:shadow-2xl transition-all duration-500 w-full">
                            <div className={`inline-block px-4 py-1 rounded-full bg-${section.gradientFrom}-100 text-${section.gradientFrom}-700 text-sm font-semibold mb-4`}>
                                Premium Service
                            </div>
                            <h2 className="text-3xl md:text-4xl font-bold mb-2 text-gray-800">
                                {section.title}
                            </h2>
                            <p className="text-xl font-medium text-gray-700 mb-4">{section.subtitle}</p>
                            <p className="text-gray-600 text-lg leading-relaxed mb-6">
                                {section.description}
                            </p>
                            <ul className="space-y-3 mb-8">
                                {section.features.map((feature, i) => (
                                    <li key={i} className="flex items-center gap-3">
                                        <div className={`w-5 h-5 rounded-full bg-${section.gradientFrom}-100 flex items-center justify-center`}>
                                            <svg className={`w-3 h-3 text-${section.gradientFrom}-600`} fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                            </svg>
                                        </div>
                                        <span className="text-gray-700">{feature}</span>
                                    </li>
                                ))}
                            </ul>
                            <button
                                onClick={() => handleBookService(section.title)}
                                className="bg-rose-600 text-white px-8 py-3 rounded-full font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 hover:bg-rose-700"
                            >
                                {section.buttonLabel}
                            </button>
                        </div>
                    </div>
                </div>
            ))}

            {/* Saree to Maxi Special Section - ORIGINAL IMAGE WITHOUT EFFECTS */}
            <div className={`my-28 transform transition-all duration-1000 delay-600 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'} relative z-10`}>
                <div className="bg-gradient-to-r from-violet-50 to-fuchsia-50 rounded-3xl p-8 md:p-12 shadow-xl border border-white/50">
                    <div className="flex flex-col md:flex-row items-center gap-10">
                        <div className="md:w-1/2">
                            <div className="inline-block px-4 py-1 rounded-full bg-violet-200 text-violet-700 text-sm font-semibold mb-4">
                                Special Transformation
                            </div>
                            <h3 className="text-3xl font-bold mb-4 text-gray-800">
                                Saree to Maxi Conversion
                            </h3>
                            <p className="text-gray-600 text-lg leading-relaxed mb-6">
                                Transform your favorite saree into a stylish maxi dress. Perfect for a modern, chic look while preserving the beauty of your saree.
                            </p>
                            <div className="flex flex-wrap gap-3 mb-8">
                                {extraServices[0].tags.map((tag, idx) => (
                                    <span key={idx} className="px-3 py-1 bg-white rounded-full text-sm text-violet-600 shadow-sm">
                                        {tag}
                                    </span>
                                ))}
                            </div>
                            <button
                                onClick={() => handleBookService("Saree to Maxi Conversion")}
                                className="bg-violet-600 text-white px-6 py-3 rounded-full font-semibold shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-300 hover:bg-violet-700"
                            >
                                Book This Transformation
                            </button>
                        </div>
                        <div className="md:w-1/2">
                            <div className="rounded-2xl overflow-hidden shadow-2xl">
                                <img
                                    src={extraServices[0].image}
                                    alt="Saree to Maxi Conversion"
                                    className="w-full h-auto object-cover"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Service Products Grid */}
            {serviceProducts.length > 0 && (
                <div className="relative z-10 my-20">
                    <div className={`text-center mb-12 transform transition-all duration-1000 delay-500 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                            Our Premium Service Collection
                        </h2>
                        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                            Explore our exclusive range of professional stitching and alteration services
                        </p>
                    </div>

                    <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 transform transition-all duration-1000 delay-600 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
                        {serviceProducts.map((product, index) => {
                            const limitWords = (text, wordLimit = 20) => {
                                if (!text) return "Professional stitching service with premium quality and perfect fit guaranteed.";
                                const words = text.trim().split(/\s+/);
                                if (words.length <= wordLimit) return text;
                                return words.slice(0, wordLimit).join(' ') + '...';
                            };

                            const limitTitle = (text, wordLimit = 8) => {
                                if (!text) return "Stitching Service";
                                const words = text.trim().split(/\s+/);
                                if (words.length <= wordLimit) return text;
                                return words.slice(0, wordLimit).join(' ') + '...';
                            };

                            const limitedDescription = limitWords(product.description, 20);
                            const limitedTitle = limitTitle(product.name, 8);
                            const optionalAttributes = product.optionalAttributes || [];
                            const gradientVariants = ['rose', 'violet', 'amber', 'fuchsia', 'orange', 'emerald'];

                            const gradientIndex = index % gradientVariants.length;
                            const gradientFrom = gradientVariants[gradientIndex];
                            const gradientTo = gradientVariants[(gradientIndex + 1) % gradientVariants.length];

                            return (
                                <div key={product.primaryId || product._id || index} className="group relative">
                                    <div className="relative h-full bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border border-gray-100">
                                        {/* Image Section - ORIGINAL IMAGE WITHOUT ANY OVERLAYS */}
                                        <div className="relative h-56 overflow-hidden">
                                            <img
                                                src={product.imageContents?.[0]?.displayLink || defaultBlouseImg}
                                                alt={product.name}
                                                className="w-full h-full object-cover"
                                                onError={(e) => { e.target.src = defaultBlouseImg; }}
                                            />
                                            {product.price?.priceAmt && (
                                                <div className="absolute top-4 right-4 z-20 bg-white/95 backdrop-blur-sm rounded-full px-4 py-2 shadow-lg">
                                                    <span className={`text-lg font-bold text-${gradientFrom}-600`}>
                                                        ${product.price.priceAmt}
                                                    </span>
                                                </div>
                                            )}
                                            <div className="absolute bottom-4 left-4 z-20">
                                                <span className={`px-3 py-1 rounded-full text-sm font-semibold bg-${gradientFrom}-100 text-${gradientFrom}-700`}>
                                                    {product.type?.typeName || 'Service'}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Content Section */}
                                        <div className="p-6">
                                            <h3 className="text-xl font-bold text-gray-800 mb-2 line-clamp-2">
                                                {limitedTitle}
                                            </h3>

                                            <div className="mb-6">
                                                <div
                                                    className="text-gray-600 text-sm leading-relaxed prose prose-sm max-w-none line-clamp-4"
                                                    dangerouslySetInnerHTML={{
                                                        __html: limitedDescription
                                                    }}
                                                />
                                                {product.description && product.description.split(/\s+/).length > 20 && (
                                                    <button
                                                        onClick={() => handleReadMore(product)}
                                                        className="mt-2 text-sm font-medium text-rose-600 hover:text-rose-700 inline-flex items-center gap-1"
                                                    >
                                                        Read more <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                                                    </button>
                                                )}
                                            </div>

                                            {optionalAttributes.length > 0 ? (
                                                <div className="space-y-3 mb-6">
                                                    {optionalAttributes.slice(0, 3).map((attr, attrIndex) => {
                                                        const extractedValues = extractValues(attr.values || []);
                                                        const isColorAttribute = hasColorValues(attr);
                                                        return (
                                                            <div key={attrIndex} className="flex items-start gap-3">
                                                                <div className={`p-1 rounded-lg mt-0.5 flex-shrink-0 bg-${gradientFrom}-50 text-${gradientFrom}-600`}>
                                                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                                                                </div>
                                                                <div className="flex-1">
                                                                    <div className="flex items-center gap-2 flex-wrap">
                                                                        <span className="text-sm font-medium text-gray-700">{attr.name || `Feature ${attrIndex + 1}`}:</span>
                                                                        <span className="text-sm text-gray-600">{extractedValues.slice(0, 3).join(', ')}{extractedValues.length > 3 && '...'}</span>
                                                                    </div>
                                                                    {isColorAttribute && extractedValues.length > 0 && (
                                                                        <div className="flex flex-wrap gap-1.5 mt-2">
                                                                            {extractedValues.slice(0, 5).map((value, vIdx) => {
                                                                                const colorMap = { red: '#FF0000', blue: '#0000FF', green: '#00FF00', yellow: '#FFFF00', black: '#000000', white: '#FFFFFF', purple: '#800080', pink: '#FFC0CB', orange: '#FFA500', brown: '#A52A2A', gray: '#808080', grey: '#808080' };
                                                                                const isHex = typeof value === 'string' && /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(value);
                                                                                const colorValue = isHex ? value : (colorMap[value.toLowerCase()] || '#CCCCCC');
                                                                                return <div key={vIdx} className="w-5 h-5 rounded-full border border-gray-200 shadow-sm" style={{ backgroundColor: colorValue }} title={value} />;
                                                                            })}
                                                                            {extractedValues.length > 5 && <span className="text-xs text-gray-500 ml-1">+{extractedValues.length - 5}</span>}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                    {optionalAttributes.length > 3 && <div className="text-center pt-2"><span className="text-xs text-gray-500">+{optionalAttributes.length - 3} more features</span></div>}
                                                </div>
                                            ) : (
                                                <div className="space-y-3 mb-6">

                                                </div>
                                            )}

                                            <div className="space-y-3">
                                                <button
                                                    onClick={() => handleBookService(product.name)}
                                                    className="w-full py-3 rounded-xl font-semibold text-white transform transition-all duration-300 hover:shadow-lg hover:scale-[1.02] bg-rose-600 hover:bg-rose-700"
                                                >
                                                    Get Consultation
                                                </button>

                                            </div>

                                            {product.createdBy && (
                                                <div className="mt-6 pt-6 border-t border-gray-100">
                                                    <p className="text-xs text-gray-500">Service by <span className="font-semibold text-gray-700">{product.createdBy}</span></p>
                                                </div>
                                            )}
                                        </div>

                                        <div className={`absolute inset-0 rounded-2xl border-2 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none border-${gradientFrom}-200`}></div>
                                    </div>
                                    <div className={`absolute -top-2 -right-2 w-4 h-4 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-${gradientFrom}-400`}></div>
                                    <div className={`absolute -bottom-2 -left-2 w-6 h-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-${gradientTo}-400`}></div>
                                </div>
                            );
                        })}
                    </div>

                </div>
            )}

            {/* Footer Contact Section - Using dynamic company details */}
            <div className={`text-center mt-24 transform transition-all duration-1000 delay-700 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'} relative z-10`}>
                <div className="bg-white/90 backdrop-blur-sm rounded-3xl p-8 md:p-10 shadow-xl border border-white/50 max-w-3xl mx-auto">
                    <div className="inline-block px-4 py-1 rounded-full bg-rose-100 text-rose-600 text-sm font-semibold mb-4">Get in Touch</div>
                    <h3 className="text-3xl font-bold mb-4 text-gray-800">
                        Ready to Transform Your Style?
                    </h3>
                    <p className="text-lg font-medium text-gray-700 mb-6">Contact us for expert stitching & alterations!</p>
                    <div className="space-y-2 text-gray-600 mb-6">
                        <p className="text-xl font-semibold text-gray-800">{contactPersonName}</p>
                        <p className="text-lg hover:text-rose-600 transition-colors duration-300">
                            {getDisplayPhoneNumber()}
                        </p>
                        <p className="text-lg hover:text-rose-600 transition-colors duration-300">{email.toLowerCase()}</p>
                    </div>
                    <button onClick={handleBookConsultation} className="bg-rose-600 text-white px-8 py-3 rounded-full font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 hover:bg-rose-700">
                        Book Consultation
                    </button>
                </div>
            </div>

            {/* PRODUCT DETAIL MODAL - COMPLETELY REDESIGNED WITH FULL VISIBILITY */}
            {showModal && selectedProduct && (
                <div className="fixed inset-0 z-50 overflow-y-auto" onClick={handleCloseModal}>
                    <div className="min-h-screen px-4 py-8 text-center">
                        {/* Darker Overlay for better focus */}
                        <div className="fixed inset-0 bg-black bg-opacity-80 transition-opacity" onClick={handleCloseModal}></div>

                        {/* Modal Panel - Enhanced with better contrast */}
                        <div className="inline-block w-full max-w-5xl my-8 text-left align-middle transition-all transform bg-white rounded-2xl shadow-2xl overflow-hidden relative z-10" onClick={(e) => e.stopPropagation()}>

                            {/* Close Button - Improved visibility */}
                            <button
                                onClick={handleCloseModal}
                                className="absolute top-4 right-4 z-20 bg-white/90 backdrop-blur-sm rounded-full p-2 shadow-lg hover:bg-gray-100 transition-colors duration-200 border border-gray-200"
                            >
                                <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>

                            <div className="flex flex-col md:flex-row max-h-[90vh] overflow-y-auto">

                                {/* Image Section - Enhanced */}
                                <div className="md:w-1/2 bg-gradient-to-br from-gray-50 to-amber-50 p-8 flex items-center justify-center md:sticky md:top-0 md:h-full">
                                    <div className="relative w-full">
                                        <div className="rounded-xl overflow-hidden shadow-2xl">
                                            <img
                                                src={selectedProduct.imageContents?.[0]?.displayLink || defaultBlouseImg}
                                                alt={selectedProduct.name}
                                                className="w-full h-auto max-h-[400px] object-contain bg-white"
                                                onError={(e) => { e.target.src = defaultBlouseImg; }}
                                            />
                                        </div>
                                        {selectedProduct.price?.priceAmt && (
                                            <div className="absolute top-4 right-4 bg-rose-600 text-white rounded-full px-5 py-2.5 shadow-lg">
                                                <span className="text-2xl font-bold">
                                                    ${selectedProduct.price.priceAmt}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Content Section - COMPLETELY ENHANCED FOR CLARITY */}
                                <div className="md:w-1/2 p-8 overflow-y-auto bg-white">

                                    {/* Service Type Badge */}
                                    <div className="mb-4">
                                        <span className="inline-block px-4 py-1.5 rounded-full text-sm font-semibold bg-rose-100 text-rose-700 border border-rose-200">
                                            {selectedProduct.type?.typeName || 'Premium Service'}
                                        </span>
                                    </div>

                                    {/* Product Title - Prominent */}
                                    <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 leading-tight">
                                        {selectedProduct.name}
                                    </h2>

                                    {/* Full Description Section - Clear and readable */}
                                    <div className="mb-8">
                                        <div className="flex items-center gap-2 mb-3">
                                            <div className="w-1 h-6 bg-rose-500 rounded-full"></div>
                                            <h3 className="text-xl font-bold text-gray-800">Description</h3>
                                        </div>
                                        <div className="bg-gray-50 rounded-xl p-5 border border-gray-100">
                                            <div
                                                className="text-gray-700 leading-relaxed prose prose-sm max-w-none"
                                                dangerouslySetInnerHTML={{
                                                    __html: selectedProduct.description || "Professional stitching service with premium quality and perfect fit guaranteed."
                                                }}
                                            />
                                        </div>
                                    </div>

                                    {/* Service Details / Optional Attributes - Enhanced with better styling */}
                                    {selectedProduct.optionalAttributes && selectedProduct.optionalAttributes.length > 0 && (
                                        <div className="mb-8">
                                            <div className="flex items-center gap-2 mb-3">
                                                <div className="w-1 h-6 bg-rose-500 rounded-full"></div>
                                                <h3 className="text-xl font-bold text-gray-800">Service Details</h3>
                                            </div>
                                            <div className="space-y-4">
                                                {selectedProduct.optionalAttributes.map((attr, idx) => {
                                                    const extractedValues = extractValues(attr.values || []);
                                                    const isColorAttribute = hasColorValues(attr);
                                                    return (
                                                        <div key={idx} className="bg-gray-50 rounded-xl p-4 border border-gray-100 hover:shadow-md transition-shadow">
                                                            <div className="flex items-start gap-3">
                                                                <div className="flex-shrink-0 w-8 h-8 bg-rose-100 rounded-lg flex items-center justify-center">
                                                                    <svg className="w-5 h-5 text-rose-600" fill="currentColor" viewBox="0 0 20 20">
                                                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                                    </svg>
                                                                </div>
                                                                <div className="flex-1">
                                                                    <h4 className="text-base font-bold text-gray-800 mb-2">{attr.name || `Feature ${idx + 1}`}</h4>
                                                                    <div className="mt-1">
                                                                        {isColorAttribute ? (
                                                                            <div className="flex flex-wrap gap-3">
                                                                                {extractedValues.map((value, vIdx) => {
                                                                                    const colorMap = { red: '#FF0000', blue: '#0000FF', green: '#00FF00', yellow: '#FFFF00', black: '#000000', white: '#FFFFFF', purple: '#800080', pink: '#FFC0CB', orange: '#FFA500', brown: '#A52A2A', gray: '#808080', grey: '#808080' };
                                                                                    const isHex = typeof value === 'string' && /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(value);
                                                                                    const colorValue = isHex ? value : (colorMap[value.toLowerCase()] || '#CCCCCC');
                                                                                    return (
                                                                                        <div key={vIdx} className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 shadow-sm border border-gray-200">
                                                                                            <div className="w-6 h-6 rounded-full border border-gray-300 shadow-inner" style={{ backgroundColor: colorValue }}></div>
                                                                                            <span className="text-sm text-gray-700 font-medium capitalize">{value}</span>
                                                                                        </div>
                                                                                    );
                                                                                })}
                                                                            </div>
                                                                        ) : (
                                                                            <div className="flex flex-wrap gap-2">
                                                                                {extractedValues.map((value, vIdx) => (
                                                                                    <span key={vIdx} className="inline-flex items-center px-3 py-1.5 bg-white rounded-lg text-sm text-gray-700 shadow-sm border border-gray-200">
                                                                                        {value}
                                                                                    </span>
                                                                                ))}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}

                                    {/* Service Provider Information */}
                                    {selectedProduct.createdBy && (
                                        <div className="mb-8 pt-2 border-t-2 border-gray-100">
                                            <div className="flex items-center gap-2 mb-2">
                                                <svg className="w-5 h-5 text-rose-500" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                                                </svg>
                                                <span className="text-sm font-semibold text-gray-600">SERVICE PROVIDER</span>
                                            </div>
                                            <p className="text-gray-800 font-medium text-lg">{selectedProduct.createdBy}</p>
                                        </div>
                                    )}

                                    {/* Call to Action Button - Prominent */}
                                    <div className="mt-6 pt-4">
                                        <button
                                            onClick={() => {
                                                handleCloseModal();
                                                handleBookService(selectedProduct.name);
                                            }}
                                            className="w-full py-4 rounded-xl font-bold text-white transform transition-all duration-300 hover:shadow-xl bg-gradient-to-r from-rose-600 to-rose-700 hover:from-rose-700 hover:to-rose-800 text-lg"
                                        >
                                            Get Consultation for {selectedProduct.name}
                                        </button>
                                        <p className="text-xs text-gray-500 text-center mt-3">
                                            Our expert will contact you shortly
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AvikarServiceDetails;
